import { parseMoney } from '@/shared/lib/money';
import type { DetalleOperacionesItem } from '@/shared/api/controllers/detalleOperaciones';

/**
 * Invoice breakdown that drives the Pagos screen's left panel. Mirrors
 * the PowerApps formulas from `Screen_DetalleVenta`/`Screen_Pagos`:
 *
 *   subtotalsByRate[rate] = Σ (precio × cantidad × (1 − desc/100))   per IVA rate
 *   subtotalDescontado    = Σ subtotalsByRate                        (full pre-IVA, post-discount)
 *   ivaTotal              = Σ subtotalsByRate[rate] × rate/100
 *   montoFactura          = subtotalDescontado × %factura / 100
 *   ivaACobrar            = ivaTotal × %factura / 100
 *   totalFactura          = montoFactura + ivaACobrar               (cap on what may be invoiced)
 *   montoCobroEfectivo    = subtotalDescontado − montoFactura       (un-invoiced portion → cash, no IVA)
 *   montoTotalCobro       = totalFactura + montoCobroEfectivo       (full amount the customer pays)
 *
 * Recargo MercadoPago por cuotas (2026-10-20): `recargoMpPct` del ABM sobre
 * el total factura SIN recargo; se suma a totalFactura (se factura) y a
 * montoTotalCobro. No toca montoFactura / ivaACobrar (base de la venta).
 * Mismo cálculo que el server (server/src/lib/recargoMp.ts).
 *
 * `%factura` only controls how much is *invoiced*; the rest is paid in
 * cash without IVA. Regalo lines (EsRegalo_DO='SI') and anuladas are
 * excluded from every sum.
 *
 * Per-rate buckets are stored as `Record<rateKey, neto>` keyed by the
 * dot-decimal rate string (`"21"`, `"10.5"`, `"16"`, `"8"`). The
 * `subtotalIva21` / `subtotalIva105` fields stay populated for AR-only
 * legacy callers; new code should iterate `subtotalsByRate`.
 */

export type InvoiceBreakdown = {
  /** Per-rate net subtotals keyed by dot-decimal rate (e.g. "21", "16"). */
  subtotalsByRate: Record<string, number>;
  /** AR backward-compat aliases. Always 0 outside AR. */
  subtotalIva21: number;
  subtotalIva105: number;
  subtotalDescontado: number;
  ivaTotal: number;
  montoFactura: number;
  ivaACobrar: number;
  totalFactura: number;
  montoCobroEfectivo: number;
  montoTotalCobro: number;
  /** % de recargo MercadoPago aplicado (0 = sin recargo). */
  recargoMpPct: number;
  /** Monto del recargo, ya incluido en totalFactura y montoTotalCobro. */
  recargoMp: number;
};

function parseIvaRate(raw: string | null | undefined): number {
  const s = (raw ?? '').trim();
  if (!s) return 0;
  if (/exento/i.test(s)) return 0;
  const n = Number(s.replace('%', '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

export function computeBreakdown(
  lines: DetalleOperacionesItem[],
  porcentajeFactura: number,
  recargoMpPct = 0,
  /** Parte (0..1) de la factura que se cobra con MercadoPago: el recargo va
   *  sólo sobre esa parte (con otra caja no-efectivo, MP cobra una porción). */
  parteMp = 1,
): InvoiceBreakdown {
  const subtotalsByRate: Record<string, number> = {};
  let subtotalDescontado = 0;
  let ivaTotal = 0;
  for (const d of lines) {
    if (d.fields.Status_DO === 'Anulado') continue;
    if (d.fields.EsRegalo_DO === 'SI') continue;
    const monto = parseMoney(d.fields.Monto_RO) ?? 0;
    const cantidad = parseMoney(d.fields.Cantidad_RO) ?? 0;
    const desc = parseMoney(d.fields.Descuento_RO) ?? 0;
    const neto = monto * cantidad * (1 - desc / 100);
    const rate = parseIvaRate(d.fields.IVA_RO);
    // Las líneas al 0% (Exento / Cliente del Exterior) suman al subtotal y al
    // total a cobrar, sólo que sin IVA; antes se descartaban y el cobro daba $0.
    if (rate < 0) continue;
    const key = String(rate);
    subtotalsByRate[key] = (subtotalsByRate[key] ?? 0) + neto;
    subtotalDescontado += neto;
    ivaTotal += neto * (rate / 100);
  }
  const pctFactor = Math.max(0, Math.min(100, porcentajeFactura)) / 100;
  const montoFactura = subtotalDescontado * pctFactor;
  const ivaACobrar = ivaTotal * pctFactor;
  const totalFacturaBase = montoFactura + ivaACobrar;
  const parte = Math.max(0, Math.min(1, parteMp));
  const recargoMp =
    recargoMpPct > 0 && totalFacturaBase > 0 ? round2((totalFacturaBase * parte * recargoMpPct) / 100) : 0;
  const totalFactura = totalFacturaBase + recargoMp;
  const montoCobroEfectivo = subtotalDescontado - montoFactura;
  const montoTotalCobro = totalFactura + montoCobroEfectivo;
  return {
    subtotalsByRate,
    subtotalIva21: subtotalsByRate['21'] ?? 0,
    subtotalIva105: subtotalsByRate['10.5'] ?? 0,
    subtotalDescontado,
    ivaTotal,
    montoFactura,
    ivaACobrar,
    totalFactura,
    montoCobroEfectivo,
    montoTotalCobro,
    recargoMpPct: recargoMp > 0 ? recargoMpPct : 0,
    recargoMp,
  };
}

/**
 * % efectivo del recargo MP guardado en una venta (monto / total factura sin
 * recargo = % nominal × parte que cobró MP). Mismo cálculo que el server
 * (`pctEfectivoRecargoMp` en server/src/lib/recargoMp.ts).
 */
export function pctEfectivoRecargoMp(totalFacturaConRecargo: number, recargoMonto: number): number {
  const base = totalFacturaConRecargo - recargoMonto;
  return recargoMonto > 0 && base > 0 ? (recargoMonto / base) * 100 : 0;
}

/** Round to 2 decimals, guards against −0 / NaN. */
export function round2(n: number): number {
  if (!Number.isFinite(n)) return 0;
  const r = Math.round(n * 100) / 100;
  return r === 0 ? 0 : r;
}
